/**
 * Multi-row selection.
 *
 * Modelled on every file manager and DAW playlist a curator already knows, so it
 * needs no learning: plain click replaces the selection, ctrl/cmd-click toggles
 * one row, shift-click extends from an anchor.
 *
 * The **anchor** is the part worth getting right. Shift-click extends from the
 * last row selected by a plain or ctrl click, not from the nearest edge of the
 * current range. Extending from the edge means a second shift-click cannot shrink
 * a range you just made too big, which feels broken the first time you try it.
 *
 * Selection is held as an ordered Set of track ids rather than indices, because
 * the list re-sorts, re-filters and pages underneath it. Indices would silently
 * point at different tracks after any of those.
 */

export interface SelectionState {
  /** Selected track ids. Insertion-ordered. */
  ids: Set<string>;
  /** Where a shift-click extends from. */
  anchor: string | null;
}

export const EMPTY_SELECTION: SelectionState = { ids: new Set(), anchor: null };

export interface ClickModifiers {
  shift?: boolean;
  /** Ctrl on Windows/Linux, Cmd on macOS. */
  meta?: boolean;
}

/**
 * Apply a click to the selection.
 *
 * `visible` is the rows in the order they are currently displayed, which is what
 * a shift-range means — the user is selecting what they can see between two
 * points, not a range in some underlying order.
 */
export function applyClick(
  state: SelectionState,
  id: string,
  visible: string[],
  mods: ClickModifiers = {}
): SelectionState {
  if (mods.shift && state.anchor !== null) {
    const from = visible.indexOf(state.anchor);
    const to = visible.indexOf(id);
    if (from !== -1 && to !== -1) {
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      // The anchor is deliberately unchanged, so a second shift-click can shrink
      // the range rather than only ever growing it.
      return { ids: new Set(visible.slice(lo, hi + 1)), anchor: state.anchor };
    }
  }

  if (mods.meta) {
    const ids = new Set(state.ids);
    if (ids.has(id)) {
      ids.delete(id);
      // Removing the anchor leaves shift-click with nothing to extend from, so
      // it moves to whatever is still selected rather than becoming stale.
      const anchor = state.anchor === id ? ([...ids].pop() ?? null) : state.anchor;
      return { ids, anchor };
    }
    ids.add(id);
    return { ids, anchor: id };
  }

  return { ids: new Set([id]), anchor: id };
}

export function selectAll(visible: string[]): SelectionState {
  return { ids: new Set(visible), anchor: visible[visible.length - 1] ?? null };
}

export function clearSelection(): SelectionState {
  return { ids: new Set(), anchor: null };
}

/**
 * Drop ids that are no longer present.
 *
 * Called when the visible set changes — a filter, a page load, a rescan. Without
 * it a selection accumulates ghosts: rows the user can no longer see but which
 * still receive a bulk action, which is the worst possible outcome for something
 * like "rescan selected".
 */
export function pruneSelection(
  state: SelectionState,
  present: Set<string>
): SelectionState {
  // The anchor is checked too, not just the ids: a stale anchor pointing at a
  // filtered-out row would make the next shift-click extend from somewhere the
  // user cannot see.
  const anchorOk = state.anchor === null || present.has(state.anchor);
  if (anchorOk && [...state.ids].every((id) => present.has(id))) return state;
  const ids = new Set([...state.ids].filter((id) => present.has(id)));
  return {
    ids,
    anchor: state.anchor && present.has(state.anchor) ? state.anchor : null,
  };
}
